import useApi from '@hooks/useApi';
import { Product, ProductCategory, ProductStatus } from '@@types/index';
import { useNavigate } from 'react-router-dom';
import { adminCategoriesAtom, adminProductsAtom } from '@jotai/admin/atoms';
import { useSetAtom } from 'jotai';
import { getAdminProductsPath } from '@constants/routes';

function useAdminProducts(workspaceId: string | undefined | null) {
  const { adminApi } = useApi();
  const navigate = useNavigate();
  const setProducts = useSetAtom(adminProductsAtom);
  const setProductCategories = useSetAtom(adminCategoriesAtom);

  const fetchProducts = () => {
    adminApi
      .get<Product[]>('/products', {
        params: {
          workspaceId: workspaceId,
        },
      })
      .then((res) => {
        setProducts(res.data);
      });
  };

  const fetchProduct = async (productId: number) => {
    const res = await adminApi.get<Product>('/product', {
      params: {
        productId,
      },
    });

    return res.data;
  };

  const createFormData = (parameter: any, file: File | null) => {
    const data = new FormData();
    data.append('body', new Blob([JSON.stringify(parameter)], { type: 'application/json' }));
    if (file) data.append('file', new Blob([file], { type: 'image/jpeg' }));

    return data;
  };

  const addProduct = (product: any, file: File) => {
    const data = createFormData(product, file);

    return adminApi.post('/product', data).catch((error) => {
      console.error('Failed to add product: ', error);
      throw error;
    });
  };

  const editProduct = (parameter: any, file: File | null) => {
    const data = createFormData(parameter, file);

    adminApi
      .put('/product', data)
      .then(() => {
        navigate(getAdminProductsPath(parameter.workspaceId));
      })
      .catch((error) => console.error('Failed to add product: ', error));
  };

  const editProductStatus = (productId: number, status: ProductStatus) => {
    adminApi
      .put('/product/status', {
        workspaceId,
        productId,
        status,
      })
      .then(() => {
        fetchProducts();
      })
      .catch((error) => {
        console.error('Failed to edit product sellable: ', error);
      });
  };

  const deleteProduct = (productId: number) => {
    adminApi
      .delete('/product', {
        params: {
          workspaceId: workspaceId,
          productId: productId,
        },
      })
      .then(() => {
        setProducts((prev) => prev.filter((item) => item.id !== productId));
      })
      .catch((error) => {
        console.error('Failed to delete selected products: ', error);
      });
  };

  const fetchCategories = () => {
    adminApi
      .get<ProductCategory[]>('/product-categories', {
        params: {
          workspaceId: workspaceId,
        },
      })
      .then((res) => {
        setProductCategories(res.data);
      })
      .catch((error) => {
        console.error('Failed to fetch products categories : ', error);
      });
  };

  const addCategory = (name: string) => {
    adminApi
      .post('/product-category', { name, workspaceId })
      .then(() => fetchCategories())
      .catch((error) => {
        console.error('Failed to add products categories : ', error);
      });
  };

  const reorderCategories = (productCategoryIds: number[]) => {
    return adminApi
      .post<ProductCategory[]>('/product-categories/sort', { workspaceId, productCategoryIds })
      .then((res) => {
        setProductCategories(res.data);
      })
      .catch((error) => {
        console.error('Failed to reorder products categories : ', error);
      });
  };

  const reorderProducts = (sorts: { categoryId: number | null; productIds: number[] }[]) => {
    return adminApi
      .put<Product[]>('/products/sort', { workspaceId, sorts })
      .then((res) => {
        setProducts(res.data);
      })
      .catch((error) => {
        console.error('Failed to reorder products : ', error);
      });
  };

  const deleteCategory = (productCategoryId: number) => {
    return adminApi.delete(`/product-category`, { params: { workspaceId, productCategoryId } }).then(() => fetchCategories());
  };

  return {
    fetchProducts,
    addProduct,
    fetchCategories,
    addCategory,
    deleteProduct,
    fetchProduct,
    editProduct,
    editProductStatus,
    reorderCategories,
    reorderProducts,
    deleteCategory,
  };
}

export default useAdminProducts;
